import { useNavigate, useParams } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, LayersFront, Pencil } from '@factorialco/f0-react/icons/app'
import {
  GroupsTableDocument,
  TrainingClassesFilterDatesDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { formatBackendDateToDayMonthYear } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { usecCreateTrainingPermissions } from 'modules/trainings/hooks/Trainings/useCreateTrainingPermissions'
import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'

export const GroupsDataTable = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { id: trainingId } = useParams()
  const { canCreate } = usecCreateTrainingPermissions()
  const client = useApolloClient()

  const source = useGraphqlDataCollectionSource({
    document: GroupsTableDocument,
    mapPagination: (data) => {
      return {
        total: data.trainings.trainingClassesConnection.totalCount ?? 0,
      }
    },
    mapData: (data) => data.trainings.trainingClassesConnection.nodes,
    search: {
      enabled: true,
    },
    sortings: {
      startDate: {
        label: i18n.t('trainings.trainings_revamp.training_classes_list.header.start_date'),
      },
      endDate: {
        label: i18n.t('trainings.trainings_revamp.training_classes_list.header.end_date'),
      },
    },
    defaultSortings: {
      field: 'startDate',
      order: 'asc',
    },
    mapVariables: ({ filters, search, sortings }) => {
      return {
        endDate: filters.endDate?.filter((d): d is string => !!d),
        startDate: filters.startDate?.filter((d): d is string => !!d),
        sort: sortings?.[0],
        search: search,
        trainingId: Number(trainingId),
      }
    },
    itemUrl: (item) =>
      navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.fullPath({
        id: String(trainingId),
        classId: String(item.id),
      }),
    filters: {
      startDate: {
        type: 'in',
        label: i18n.t('trainings.trainings_revamp.training_classes_list.filters.start_date'),
        options: {
          options: async () => {
            const { data } = await client.query({
              query: TrainingClassesFilterDatesDocument,
              variables: {
                trainingId: Number(trainingId),
              },
              fetchPolicy: 'cache-first',
            })
            const dates = data.trainings.trainingClassesConnection.nodes
              .map((group) => group.startDate)
              .filter((date): date is string => !!date)

            const uniqueDates = Array.from(new Set(dates))

            return uniqueDates.map((date) => ({
              label: String(date),
              value: date,
            }))
          },
        },
      },
      endDate: {
        type: 'in',
        label: i18n.t('trainings.trainings_revamp.training_classes_list.filters.end_date'),
        options: {
          options: async () => {
            const { data } = await client.query({
              query: TrainingClassesFilterDatesDocument,
              variables: {
                trainingId: Number(trainingId),
              },
              fetchPolicy: 'cache-first',
            })

            const dates = data.trainings.trainingClassesConnection.nodes
              .map((group) => group.endDate)
              .filter((date): date is string => !!date)

            const uniqueDates = Array.from(new Set(dates))

            return uniqueDates.map((date) => ({
              label: String(date),
              value: date,
            }))
          },
        },
      },
    },
    primaryActions: () =>
      canCreate
        ? {
            label: i18n.t('trainings.trainings_revamp.training_classes_list.new_group'),
            icon: Add,
            onClick: () => {
              ;(track(trackingEvents.training.trainingGroupCreationWizard.goToNewGroupWizard),
                navigate(
                  navigation.trainings.list.details_revamp.view.trainingClasses.tab.newClass.fullPath(
                    {
                      id: Number(trainingId),
                    }
                  )
                ))
            },
          }
        : undefined,
    itemActions: (item) => [
      {
        label: i18n.t('trainings.trainings_revamp.classes.actions.edit'),
        icon: Pencil,
        onClick: () => {
          track(trackingEvents.training.trainingClass.editClass)
          navigate(
            navigation.trainings.list.details_revamp.view.trainingClasses.tab.editClassModal.fullPath(
              {
                id: Number(trainingId),
                classId: Number(item.id),
              }
            )
          )
        },
      },
      {
        label: i18n.t('trainings.trainings_revamp.classes.actions.duplicate_group'),
        icon: LayersFront,
        onClick: () => {
          navigate(
            navigation.trainings.list.details_revamp.view.trainingClasses.tab.duplicateClass.fullPath(
              {
                id: Number(trainingId),
                classId: Number(item.id),
              }
            )
          )
        },
      },
      {
        label: i18n.t('trainings.trainings_revamp.classes.actions.delete_group'),
        icon: Delete,
        critical: true,
        onClick: () => {
          navigate(
            navigation.trainings.list.details_revamp.view.trainingClasses.tab.deleteClass.fullPath({
              id: Number(trainingId),
              classId: Number(item.id),
            })
          )
        },
      },
    ],
  })

  return (
    <OneDataCollection
      id="trainings/groups/v1"
      storage={DEFAULT_DATA_COLLECTION_STORAGE}
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('trainings.trainings_revamp.training_classes_list.header.group'),
                render: (item) => ({ type: 'text', value: item.name }),
              },
              {
                label: i18n.t('trainings.trainings_revamp.training_classes_list.header.start_date'),
                sorting: 'startDate',
                render: (item) => ({
                  type: 'text',
                  value: item.startDate ? formatBackendDateToDayMonthYear(item.startDate) : '',
                }),
              },
              {
                label: i18n.t('trainings.trainings_revamp.training_classes_list.header.end_date'),
                sorting: 'endDate',
                render: (item) => ({
                  type: 'text',
                  value: item.endDate ? formatBackendDateToDayMonthYear(item.endDate) : '',
                }),
              },
              {
                label: i18n.t('trainings.trainings_revamp.training_classes_list.header.sessions'),
                render: (item) => ({
                  type: 'text',
                  value: String(item.sessionsConnection.totalCount ?? 0),
                }),
              },
              {
                label: i18n.t(
                  'trainings.trainings_revamp.training_classes_list.header.participants'
                ),
                render: (item) => ({
                  type: 'avatarList',
                  value: {
                    avatarList: item.classMembershipsConnection.nodes.map((cm) => ({
                      firstName: cm.access?.firstName ?? '',
                      lastName: cm.access?.lastName ?? '',
                      type: 'person',
                      src: buildUrl(cm.access?.avatar) ?? '',
                    })),
                    max: 5,
                  },
                }),
              },
              {
                label: i18n.t(
                  'trainings.trainings_revamp.training_classes_list.header.participation_rate'
                ),
                render: (item) => {
                  const completed = Number(item.completedAttendancesCount ?? 0)
                  const total = Number(item.totalAttendancesCount ?? 0)
                  const percentage = total > 0 ? Number(((completed / total) * 100).toFixed(1)) : 0
                  return {
                    type: 'percentage',
                    value: {
                      label: `${percentage}%`,
                      percentage: percentage,
                    },
                  }
                },
              },
            ],
          },
        },
      ]}
    />
  )
}
